import { useDragLayer, useDrop } from "react-dnd";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useGetUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { useSearch } from "@/utils";

import { ToDoStatus } from "../../ToDo/ToDoList/enum/ToDoStatus.enum";
import { useToDoSocket } from "../../ToDo/ToDoList/providers/SocketContext";
import ToDoItem from "../../ToDo/ToDoList/components/ToDoItem";
import { SubTaskInfo, TaskInfo } from "../../TasksAndSubTasks/schema/tasks-and-subtasks";
import AssigneeModal from "../../TasksAndSubTasks/components/AssigneeModal";
import { useGetAssigneesQuery } from "@/pages/Private/redux/tasks-and-subtasks/tasks-and-subtasks.api";

interface SwimlaneColumnProps {
	data: (TaskInfo | SubTaskInfo)[];
	status: ToDoStatus;
	showTaskDetails: (taskId: number) => void;
	showSubTaskDetails?: (subTaskId: number) => void;
	allItems: (TaskInfo | SubTaskInfo)[];
}

export default function SwimlaneColumn({
	data,
	status,
	showTaskDetails,
	showSubTaskDetails,
	allItems,
}: SwimlaneColumnProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);
	const { changeTaskStatus, changeSubTaskStatus } = useToDoSocket();
	const [isAssigneeModalOpen, setIsAssigneeModalOpen] = useState(false);
	const [droppedData, setDroppedData] = useState<TaskInfo | SubTaskInfo | null>(null);
	const [droppedItem, setDroppedItem] = useState<{
		taskId?: number;
		subTaskId?: number;
		status: ToDoStatus;
	} | null>(null);
	const [expandedTaskIds, setExpandedTaskIds] = useState<Set<number>>(new Set());

	const toggleExpanded = (taskId: number) => {
		setExpandedTaskIds((prev) => {
			const next = new Set(prev);

			if (next.has(taskId)) {
				next.delete(taskId);
			} else {
				next.add(taskId);
			}

			return next;
		});
	};
	const [{ isOverColumn }, drop] = useDrop({
		accept: ["TODO_ITEM", "TODO_SUB_ITEM"],
		drop: (item: { taskId?: number; subTaskId?: number; status: ToDoStatus }, monitor) => {
			const type = monitor.getItemType();

			if (item?.status === status) {
				return;
			}

			if (status === ToDoStatus.QA) {
				const found = item.subTaskId
					? allItems.find((d) => !d.isTask && d.id === item.subTaskId) ?? null
					: allItems.find((d) => d.isTask && d.id === item.taskId) ?? null;

				setDroppedItem(item);
				setDroppedData(found);
				setIsAssigneeModalOpen(true);

				return;
			}

			if (type === "TODO_ITEM" && item.taskId) {
				changeTaskStatus(item.taskId, status);
			} else if (type === "TODO_SUB_ITEM" && item.subTaskId && item.taskId) {
				changeSubTaskStatus(item.taskId, item?.subTaskId, status);
			}
		},
		canDrop: (item: { taskId?: number; subTaskId?: number; status: ToDoStatus }) => {
			if (status === ToDoStatus.DONE && item.status !== ToDoStatus.QA) {
				return false;
			}

			return true;
		},
		collect: (monitor) => ({
			isOverColumn: monitor.isOver() && monitor.canDrop() && monitor.getItem()?.status !== status,
		}),
	});

	const { draggingItem } = useDragLayer((monitor) => ({
		draggingItem: monitor.isDragging() ? monitor.getItem() : null,
	}));

	const isBlocked =
		!!draggingItem && status === ToDoStatus.DONE && draggingItem.status !== ToDoStatus.QA;

	const { id } = useParams();
	const search = useSearch();

	const { data: users } = useGetAssigneesQuery();

	const groups = useMemo<{ mainTask: TaskInfo | null; subTasks: SubTaskInfo[] }[]>(() => {
		const result: { mainTask: TaskInfo | null; subTasks: SubTaskInfo[] }[] = [];
		const items = [...data];
		let i = 0;

		while (i < items.length) {
			const item = items[i];

			if (item.isTask) {
				const subTasks: SubTaskInfo[] = [];
				let j = i + 1;

				while (
					j < items.length &&
					!items[j].isTask &&
					(items[j] as unknown as SubTaskInfo).taskId === item.id
				) {
					subTasks.push(items[j] as unknown as SubTaskInfo);
					j++;
				}

				result.push({ mainTask: item as unknown as TaskInfo, subTasks });
				i = j;
			} else {
				const targetTaskId = (items[i] as unknown as SubTaskInfo).taskId;
				const matched: SubTaskInfo[] = [];
				const unmatched: (typeof items)[number][] = [];

				while (i < items.length && !items[i].isTask) {
					const sub = items[i] as unknown as SubTaskInfo;

					if (sub.taskId === targetTaskId) {
						matched.push(sub);
					} else {
						unmatched.push(items[i]);
					}

					i++;
				}

				result.push({ mainTask: null, subTasks: matched });
				items.splice(i, 0, ...unmatched);
			}
		}

		return result;
	}, [data]);

	return (
		<div
			ref={drop}
			className={`flex-1 min-w-0 border border-primary-gray-lightest p-1 rounded-xl  relative bg-surface ${
				isOverColumn ? "ring-2 ring-accent" : ""
			} 
			`}
		>
			<div className="space-y-1 min-h-[120px] max-h-[300px] overflow-y-auto custom-scrollbar p-1">
				{isOverColumn && (
					<div className="w-full h-16 border border-dashed rounded-xl border-accent flex items-center justify-center">
						<p className="text-[12px] text-subtle text-center">
							{ts("change-task-status")} &quot;{ts(status)}&quot;
						</p>
					</div>
				)}
				{data?.length ? (
					groups.map((group, idx) => {
						const isStandaloneTask = group.mainTask && group.subTasks.length === 0;
						const isOrphanGroup = !group.mainTask;

						if (isStandaloneTask) {
							return (
								<ToDoItem
									key={group.mainTask!.id}
									data={group.mainTask!}
									isSubTask={false}
									showTaskDetails={showTaskDetails}
									users={users ?? null}
								/>
							);
						}

						const mainTaskId = group.mainTask?.id;
						const hasMainWithSubs = !!group.mainTask && group.subTasks.length > 0;
						const isCollapsed =
							hasMainWithSubs && !(mainTaskId !== undefined && expandedTaskIds.has(mainTaskId));

						return (
							<div key={idx} className="rounded-xl bg-gray-700/10 p-1 space-y-1">
								{group.mainTask && (
									<ToDoItem
										data={group.mainTask}
										hasSubTasks={hasMainWithSubs}
										isSubTask={false}
										isSubTasksCollapsed={isCollapsed}
										showTaskDetails={showTaskDetails}
										subTasksCount={group.subTasks.length}
										users={users ?? null}
										onToggleSubTasks={() => {
											if (mainTaskId !== undefined) {
												toggleExpanded(mainTaskId);
											}
										}}
									/>
								)}
								{!isCollapsed &&
									group.subTasks.map((sub, subIdx) => (
										<ToDoItem
											key={sub.id}
											data={sub}
											isLastSubTask={subIdx === group.subTasks.length - 1}
											isOrphanGroup={isOrphanGroup}
											isSubTask={true}
											showParentLabel={subIdx === 0}
											showTaskDetails={() => {
												if (sub && sub.id) {
													showSubTaskDetails?.(sub.id);
												}
											}}
											users={users ?? null}
										/>
									))}
							</div>
						);
					})
				) : isBlocked ? (
					<div className="text-[16px] text-subtle text-center py-4 cursor-not-allowed">
						<p className="text-[14px] text-subtle text-center py-4">
							{draggingItem.subTaskId
								? ts("cannot-move-subtask-to-done")
								: ts("cannot-move-task-to-done")}
						</p>
					</div>
				) : (
					<p className="text-[16px] text-subtle text-center py-4">{ts("list-empty")}</p>
				)}
			</div>
			{droppedData && (
				<AssigneeModal
					data={droppedData}
					isOpen={isAssigneeModalOpen}
					setIsOpen={(val) => {
						setIsAssigneeModalOpen(val);
						if (!val) {
							setDroppedData(null);
						}
					}}
				/>
			)}{" "}
		</div>
	);
}
